import * as i0 from '@angular/core';
import { InjectionToken, HostListener, Input, Optional, Inject, Directive } from '@angular/core';
import * as i1 from '@angular/forms';
import { Subject } from 'rxjs';
import { tap, takeUntil } from 'rxjs/operators';
const AUTO_SIZE_INPUT_OPTIONS = new InjectionToken('autosize-input-options');
const DEFAULT_AUTO_SIZE_INPUT_OPTIONS = {
  extraWidth: 0,
  includeBorders: false,
  includePadding: true,
  includePlaceholder: true,
  maxWidth: -1,
  minWidth: -1,
  setParentWidth: false,
  usePlaceHolderWhenEmpty: false
};
var Border = /*#__PURE__*/function (Border) {
  Border["left"] = "border-left-width";
  Border["right"] = "border-right-width";
  return Border;
}(Border || {});
var Padding = /*#__PURE__*/function (Padding) {
  Padding["left"] = "padding-left";
  Padding["right"] = "padding-right";
  return Padding;
}(Padding || {});
let AutoSizeInputDirective = /*#__PURE__*/(() => {
  class AutoSizeInputDirective {
    constructor(element, renderer, ngModel, ngControl, options) {
      this.element = element;
      this.renderer = renderer;
      this.ngModel = ngModel;
      this.ngControl = ngControl;
      this.options = options;
      this.useValueProperty = false;
      this.destroyed$ = new Subject();
      const defaultOptions = this.options || DEFAULT_AUTO_SIZE_INPUT_OPTIONS;
      this.extraWidth = defaultOptions.extraWidth;
      this.includeBorders = defaultOptions.includeBorders;
      this.includePadding = defaultOptions.includePadding;
      this.includePlaceholder = defaultOptions.includePlaceholder;
      this.maxWidth = defaultOptions.maxWidth;
      this.minWidth = defaultOptions.minWidth;
      this.setParentWidth = defaultOptions.setParentWidth;
      this.usePlaceHolderWhenEmpty = defaultOptions.usePlaceHolderWhenEmpty;
    }
    get borderWidth() {
      return this.includeBorders ? this.sumStylePropertyWidths(Border.left, Border.right) : 0;
    }
    get paddingWidth() {
      return this.includePadding ? this.sumStylePropertyWidths(Padding.left, Padding.right) : 0;
    }
    get style() {
      return getComputedStyle(this.element.nativeElement, '');
    }
    get placeholder() {
      return this.element.nativeElement.placeholder;
    }
    get value() {
      return this.element.nativeElement.value;
    }
    get inputWidthIsLessThanMinimum() {
      return this.minWidth > 0 && this.minWidth > this.inputTextWidth;
    }
    get inputWidthIsGreaterThanMaximum() {
      return this.maxWidth > 0 && this.maxWidth < this.inputTextWidth;
    }
    get inputTextWidth() {
      return this.getTextWidth(this.input) + this.extraWidth + this.borderWidth + this.paddingWidth;
    }
    get usePlaceholder() {
      return this.includePlaceholder && this.hasPlaceholder && this.placeHolderWidthGreaterThanInputWidth && (this.inputIsEmpty || !this.usePlaceHolderWhenEmpty);
    }
    get placeHolderWidthGreaterThanInputWidth() {
      const placeHolderWidth = this.getTextWidth(this.placeholder);
      const inputWidth = this.getTextWidth(this.input);
      return placeHolderWidth > inputWidth;
    }
    get inputIsEmpty() {
      return this.input.length === 0;
    }
    get hasPlaceholder() {
      return this.placeholder.length > 0;
    }
    get input() {
      let value = '';
      if (this.useValueProperty) {
        value = this.value;
      } else if (this.ngModel) {
        value = this.ngModel.value;
      } else if (this.ngControl) {
        value = this.ngControl.value;
      }
      return value || this.value || '';
    }
    get nativeElement() {
      return this.element.nativeElement;
    }
    get textWidth() {
      const text = this.usePlaceholder ? this.placeholder : this.input;
      return this.borderWidth + this.extraWidth + this.getTextWidth(text) + this.paddingWidth;
    }
    ngAfterViewInit() {
      this.updateWidthWhenModelChanges();
      this.updateWidthWhenControlChanges();
      this.updateWidth();
    }
    ngOnDestroy() {
      this.destroyed$.next();
      this.destroyed$.complete();
    }
    onInput() {
      if (!this.ngModel && !this.ngControl) {
        this.updateWidth();
      }
    }
    setWidth(width) {
      const parent = this.renderer.parentNode(this.nativeElement);
      if (this.setParentWidth) {
        this.renderer.setStyle(parent, 'width', width + 'px');
      } else {
        this.renderer.setStyle(this.nativeElement, 'width', width + 'px');
      }
    }
    getTextWidth(value) {
      const element = this.renderer.createElement('canvas');
      const context = element.getContext('2d');
      const {
        fontStyle,
        fontVariant,
        fontWeight,
        fontSize,
        fontFamily,
        letterSpacing
      } = this.style;
      // font string format: {normal, normal, 700, 20px, Roboto, "Helvetica Neue", sans-serif}
      context.font = `${fontStyle} ${fontVariant} ${fontWeight} ${fontSize} ${fontFamily}`;
      context.letterSpacing = letterSpacing;
      const width = context.measureText(value).width;
      element.remove();
      return width;
    }
    updateWidth() {
      if (this.inputWidthIsLessThanMinimum) {
        this.setWidth(this.minWidth);
      } else if (this.inputWidthIsGreaterThanMaximum) {
        this.setWidth(this.maxWidth);
      } else {
        this.setWidth(this.textWidth);
      }
    }
    updateWidthWhenControlChanges() {
      return this.ngControl?.valueChanges?.pipe(tap(() => this.updateWidth()), takeUntil(this.destroyed$)).subscribe();
    }
    updateWidthWhenModelChanges() {
      return this.ngModel?.valueChanges?.pipe(tap(() => this.updateWidth()), takeUntil(this.destroyed$)).subscribe();
    }
    sumStylePropertyWidths(...properties) {
      return properties.reduce((sum, property) => {
        const value = this.style.getPropertyValue(property);
        const width = parseInt(value, 10);
        return sum + width;
      }, 0);
    }
    static {
      this.ɵfac = function AutoSizeInputDirective_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || AutoSizeInputDirective)(i0.ɵɵdirectiveInject(i0.ElementRef), i0.ɵɵdirectiveInject(i0.Renderer2), i0.ɵɵdirectiveInject(i1.NgModel, 8), i0.ɵɵdirectiveInject(i1.NgControl, 8), i0.ɵɵdirectiveInject(AUTO_SIZE_INPUT_OPTIONS, 8));
      };
    }
    static {
      this.ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
        type: AutoSizeInputDirective,
        selectors: [["", "autoSizeInput", ""]],
        hostBindings: function AutoSizeInputDirective_HostBindings(rf, ctx) {
          if (rf & 1) {
            i0.ɵɵlistener("input", function AutoSizeInputDirective_input_HostBindingHandler() {
              return ctx.onInput();
            });
          }
        },
        inputs: {
          extraWidth: "extraWidth",
          includeBorders: "includeBorders",
          includePadding: "includePadding",
          includePlaceholder: "includePlaceholder",
          maxWidth: "maxWidth",
          minWidth: "minWidth",
          setParentWidth: "setParentWidth",
          usePlaceHolderWhenEmpty: "usePlaceHolderWhenEmpty",
          useValueProperty: "useValueProperty"
        }
      });
    }
  }
  return AutoSizeInputDirective;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let CdkInputAutosizeDirective = /*#__PURE__*/(() => {
  class CdkInputAutosizeDirective {
    constructor(_elementRef) {
      this._elementRef = _elementRef;
      this.cdkInputAutosizeUsePlaceHolderOnlyWhenEmpty = true;
      this._initialWidth = '0px';
    }
    get _inputElement() {
      return this._elementRef.nativeElement;
    }
    ngAfterViewInit() {
      this._resizeToFitContent();
    }
    ngDoCheck() {
      this._resizeToFitContent();
    }
    _createInputWithValue(value) {
      const clone = this._inputElement.cloneNode(false);
      clone.value = value;
      clone.style.position = 'absolute';
      clone.style.border = 'none';
      clone.style.padding = '0';
      clone.style.height = '';
      clone.style.minWidth = '';
      clone.style.width = '0px';
      clone.style.maxWidth = '';
      this._inputElement.parentNode?.appendChild(clone);
      return clone;
    }
    _getWidths() {
      const inputClone = this._createInputWithValue(this._inputElement.value);
      const placeHolderClone = this._createInputWithValue(this._inputElement.placeholder);
      const widths = {
        inputWidth: inputClone.scrollWidth,
        placeholderWidth: placeHolderClone.scrollWidth
      };
      inputClone.remove();
      placeHolderClone.remove();
      return widths;
    }
    _resizeToFitContent() {
      const {
        inputWidth,
        placeholderWidth
      } = this._getWidths();
      let width;
      if (this.cdkInputAutosizeUsePlaceHolderOnlyWhenEmpty) {
        width = this._inputElement.value === '' ? placeholderWidth : inputWidth;
      } else {
        width = placeholderWidth >= inputWidth ? placeholderWidth : inputWidth;
      }
      if (this.cdkInputAutosizeMinWidth !== undefined) {
        width = Math.max(width, this.cdkInputAutosizeMinWidth);
      }
      if (this.cdkInputAutosizeMaxWidth !== undefined) {
        width = Math.min(width, this.cdkInputAutosizeMaxWidth);
      }
      this._elementRef.nativeElement.style.width = width + 'px';
    }
    static {
      this.ɵfac = function CdkInputAutosizeDirective_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || CdkInputAutosizeDirective)(i0.ɵɵdirectiveInject(i0.ElementRef));
      };
    }
    static {
      this.ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
        type: CdkInputAutosizeDirective,
        selectors: [["input", "cdkInputAutosize", ""]],
        hostAttrs: [1, "cdk-input-autosize"],
        hostVars: 2,
        hostBindings: function CdkInputAutosizeDirective_HostBindings(rf, ctx) {
          if (rf & 1) {
            i0.ɵɵlistener("input", function CdkInputAutosizeDirective_input_HostBindingHandler() {
              return ctx._resizeToFitContent();
            });
          }
          if (rf & 2) {
            i0.ɵɵstyleProp("width", ctx._initialWidth);
          }
        },
        inputs: {
          cdkInputAutosizeMinWidth: "cdkInputAutosizeMinWidth",
          cdkInputAutosizeMaxWidth: "cdkInputAutosizeMaxWidth",
          cdkInputAutosizeUsePlaceHolderOnlyWhenEmpty: "cdkInputAutosizeUsePlaceHolderOnlyWhenEmpty"
        },
        exportAs: ["cdkInputAutosize"]
      });
    }
  }
  return CdkInputAutosizeDirective;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * Generated bundle index. Do not edit.
 */

export { AUTO_SIZE_INPUT_OPTIONS, AutoSizeInputDirective, CdkInputAutosizeDirective, DEFAULT_AUTO_SIZE_INPUT_OPTIONS };
